/**
 * Cross-tool time correlation for Amplitude vs GKE logs vs Sentry.
 *
 * - GKE pods and Node servers use UTC wall clock.
 * - Sentry event timestamps are UTC.
 * - Amplitude Dashboard REST API buckets dates in the **project timezone**
 *   (see Amplitude project settings); raw events carry client event time.
 *
 * These helpers attach explicit UTC and IANA timezone hints so charts,
 * exports, and Sentry/GKE log lines can be lined up in postmortems.
 */

import type { ServerZoneType } from "@amplitude/analytics-core";

/** Ingestion region — must match Amplitude project (US vs EU residency). */
export function resolveAmplitudeServerZoneFromEnv(
  env: NodeJS.ProcessEnv
): ServerZoneType {
  const raw = (
    env.NEXT_PUBLIC_AMPLITUDE_SERVER_ZONE ||
    env.AMPLITUDE_SERVER_ZONE ||
    "US"
  )
    .trim()
    .toUpperCase();
  return raw === "EU" ? "EU" : "US";
}

/** Browser-only: IANA zone + JS offset for event property enrichment. */
export function getBrowserTimeCorrelationProps(): {
  emit_iso_utc: string;
  iana_timezone: string;
  js_timezone_offset_mins: number;
} {
  const now = new Date();
  const iana =
    typeof Intl !== "undefined" &&
    typeof Intl.DateTimeFormat === "function" &&
    typeof Intl.DateTimeFormat().resolvedOptions === "function"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown"
      : "unknown";
  return {
    emit_iso_utc: now.toISOString(),
    iana_timezone: iana,
    js_timezone_offset_mins: now.getTimezoneOffset(),
  };
}

/** Node server: UTC ISO + explicit note that emit time is server (UTC) clock. */
export function getServerTimeCorrelationProps(): {
  emit_iso_utc: string;
  emitter_clock: "UTC";
} {
  return {
    emit_iso_utc: new Date().toISOString(),
    emitter_clock: "UTC",
  };
}
